/**
 * Browser-side retrieval of the caller's Midway id_token, forwarded to the backend as
 * `X-Client-IDToken` (see {@link src/api/authLink}).
 *
 * TRANSCOM requires a human identity in the TransitiveAuth token LangHub presents, which the
 * Cognito JWT alone cannot supply: the backend exchanges this id_token with CloudAuth
 * (`putSubjectToken`) for an employee TA token after checking its `sub` claim against the Cognito
 * user. The browser is the only place the token can be obtained, because it is issued against the
 * user's Midway session cookie.
 *
 * Everything here is **best-effort and non-fatal**: every failure resolves `null`, the header is
 * then omitted, and the backend falls back to today's service-identity behavior. In particular this
 * module never redirects to Midway login — losing the employee identity is strictly preferable to
 * navigating a user away mid-session.
 *
 * Midway only issues id_tokens to relying parties registered for the exact `client_id` below (the
 * serving origin), and only for identities enrolled against that `client_id` in Federate. The
 * beta/gamma/prod origins are registered; personal stacks are not — neither their
 * `*.cloudfront.net` distribution nor their `<alias>-test.langhub.localization.amazon.dev` alias —
 * so Midway answers 400 with login HTML and this resolves `null` there, leaving TRANSCOM calls on
 * the service (NPE) identity by design (verified 2026-09-29 against both personal-stack origins).
 */

const MIDWAY_SSO_URL = 'https://midway-auth.amazon.com/SSO';

/** Stop serving a token this long before its `exp`, so it cannot expire in flight. */
export const EXPIRY_SKEW_MS = 60_000;

/** How long one failure suppresses further requests, so a signed-out user never hammers Midway. */
export const FAILURE_BACKOFF_MS = 60_000;

/**
 * Cap on the Midway round-trip. `authLink` awaits this call before letting a mutation go out, and
 * concurrent callers share the same promise, so an unbounded request would stall traffic rather
 * than degrade it. Timing out yields `null`, which is the same "no employee identity" outcome as
 * any other failure.
 */
export const FETCH_TIMEOUT_MS = 3_000;

/**
 * Three non-empty base64url segments. Shape is the only reliable success signal here: Midway
 * answers a request with no usable session by returning its HTML login page with a 200, so the
 * status code alone would let markup through as a token.
 */
const JWT_SHAPE = /^[\w-]+\.[\w-]+\.[\w-]+$/;

interface CachedToken {
  token: string;
  /** Epoch millis after which the token is no longer served (its `exp` minus the skew). */
  usableUntilMs: number;
}

// Module scope, so every GraphQL request in a page session shares one token and one Midway
// round-trip rather than fetching per request.
let cachedToken: CachedToken | null = null;
let retryAfterMs = 0;
let inFlight: Promise<string | null> | null = null;

const buildSsoUrl = (): string => {
  const url = new URL(MIDWAY_SSO_URL);
  const { origin } = window.location;

  url.searchParams.set('response_type', 'id_token');
  // Midway issues the id_token to the relying party named by client_id/redirect_uri and rejects a
  // request whose redirect_uri does not match, so both must be the live origin rather than a
  // hardcoded host — the app is served from a different origin per stage and in local dev.
  url.searchParams.set('client_id', origin);
  url.searchParams.set('redirect_uri', origin);
  url.searchParams.set('scope', 'openid');
  url.searchParams.set('nonce', crypto.randomUUID());

  return url.toString();
};

/**
 * Epoch millis at which `token` expires, or `null` when the payload cannot be read. Reads `exp`
 * only — no other claim is extracted, logged, or returned anywhere in this module.
 */
const readExpiryMs = (token: string): number | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    // JWT `exp` is epoch SECONDS per RFC 7519 — deliberately different from LangHub API
    // timestamps, which are epoch milliseconds. Do not remove the *1000.
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const fetchIdToken = async (): Promise<string | null> => {
  try {
    const response = await fetch(buildSsoUrl(), {
      credentials: 'include',
      // Bounded so a stalled Midway degrades instead of hanging; the abort rejects the fetch, which
      // the catch below turns into `null` like any other failure.
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!response.ok) return null;

    const body = (await response.text()).trim();
    if (!JWT_SHAPE.test(body)) return null;

    const expiryMs = readExpiryMs(body);
    const usableUntilMs = expiryMs === null ? null : expiryMs - EXPIRY_SKEW_MS;
    // An unreadable `exp` leaves the token's lifetime unknown, and one already inside the skew is
    // unusable now. Both are failures rather than successes-with-a-stale-token: returning the token
    // would forward a credential the backend may reject, and — because a non-null return skips the
    // negative cache — would also re-fetch from Midway on every subsequent operation.
    if (usableUntilMs === null || usableUntilMs <= Date.now()) return null;

    cachedToken = { token: body, usableUntilMs };
    return body;
  } catch {
    return null;
  }
};

/**
 * The caller's Midway id_token, or `null` when one cannot be obtained — which callers must treat
 * as "send no employee identity", never as an error.
 *
 * Cached until shortly before expiry; concurrent callers share a single in-flight request, and a
 * failure is remembered for {@link FAILURE_BACKOFF_MS} so a signed-out session does not issue a
 * Midway request per GraphQL operation. Resolves within {@link FETCH_TIMEOUT_MS} of the first
 * uncached call, so awaiting it cannot stall an operation indefinitely.
 */
export const getMidwayIdToken = (): Promise<string | null> => {
  // Local/no-auth builds have no Cognito user for the backend to match the token's `sub` against,
  // so the exchange could only be rejected. Skip the request entirely.
  if (NO_AUTH) return Promise.resolve(null);

  const now = Date.now();
  if (cachedToken && now < cachedToken.usableUntilMs) {
    return Promise.resolve(cachedToken.token);
  }
  if (now < retryAfterMs) return Promise.resolve(null);
  if (inFlight) return inFlight;

  inFlight = fetchIdToken()
    .then((token) => {
      if (token === null) retryAfterMs = Date.now() + FAILURE_BACKOFF_MS;
      return token;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Clears the module-scope token, backoff and in-flight state.
 *
 * Test-only: that state is deliberately module-level so one token serves a whole page session,
 * which also means it would otherwise leak between tests in the same file.
 */
export const resetMidwayTokenCacheForTests = (): void => {
  cachedToken = null;
  retryAfterMs = 0;
  inFlight = null;
};
